import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Avatar, 
  Grid, 
  Skeleton, 
  Container,
  Button,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Fade,
  Paper,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Group as GroupIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Inbox as InboxIcon,
  Login as LoginIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { OrganizationInvitationCard } from '../Organization/OrganizationInvitations';

const InvitationCard = ({ invitation, onUpdate, onSpaceAdded }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState({ accept: false, decline: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleResponse = async (accepted) => {
    setLoading(prev => ({ ...prev, [accepted ? 'accept' : 'decline']: true }));
    
    try {
      await axios.post(`${API_URL}/invitations/${invitation.invitation_id}`, {
        accepted
      });
      
      setSnackbar({
        open: true,
        message: accepted ? 'Successfully joined the space!' : 'Invitation declined',
        severity: 'success'
      });
      
      if (onUpdate) {
        onUpdate(invitation.invitation_id);
      }

      // If invitation was accepted, refresh spaces
      if (accepted && onSpaceAdded) {
        onSpaceAdded();
      }
    } catch (error) {
      console.error('Error responding to invitation:', error);
      setSnackbar({
        open: true,
        message: 'Failed to process invitation. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, [accepted ? 'accept' : 'decline']: false }));
    }
  };

  return (
    <>
      <Fade in timeout={500}>
        <Card 
          sx={{ 
            mb: 3,
            position: 'relative',
            overflow: 'visible',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: theme.shadows[8],
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              borderRadius: '4px 4px 0 0',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
            },
            '&:hover::before': {
              opacity: 1,
            }
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Avatar
                src={invitation.profile_image_url}
                alt={`${invitation.first_name} ${invitation.last_name}`}
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mr: 2,
                  border: `2px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                {invitation.first_name?.[0]}{invitation.last_name?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    {invitation.first_name} {invitation.last_name}
                  </Typography>
                  <Chip
                    icon={<GroupIcon />}
                    label="Space Invitation"
                    size="small"
                    color="primary"
                    sx={{ 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiChip-icon': { color: theme.palette.primary.main }
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Invited you to join their space
                </Typography>
              </Box>
            </Box>
            
            <Paper 
              elevation={0}
              sx={{ 
                position: 'relative', 
                height: 160, 
                mb: 2, 
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.02)',
                }
              }}
            >
              {invitation.image_url ? (
                <img 
                  src={invitation.image_url} 
                  alt={invitation.space_name}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
              ) : (
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 2,
                  textAlign: 'center'
                }}>
                  <GroupIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                    {invitation.space_name}
                  </Typography>
                </Box>
              )}
            </Paper>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {invitation.space_name}
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  lineHeight: 1.6,
                  mb: 2
                }}
              >
                {invitation.description}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={loading.decline ? <CircularProgress size={20} color="error" /> : <CloseIcon />}
                onClick={() => handleResponse(false)}
                disabled={loading.accept || loading.decline}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  }
                }}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="primary"
                endIcon={loading.accept ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                onClick={() => handleResponse(true)}
                disabled={loading.accept || loading.decline}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }
                }}
              >
                Accept
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const InvitationSkeleton = () => (
  <Card sx={{ mb: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Skeleton variant="text" width={120} height={24} sx={{ mr: 1 }} />
            <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton variant="text" width={180} height={20} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={160} sx={{ mb: 2, borderRadius: 2 }} />
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="100%" height={24} sx={{ mb: 2 }} />
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 2 }} />
      </Box>
    </CardContent>
  </Card>
);

const NoInvitationsMessage = () => {
  const theme = useTheme();
  return (
    <Fade in timeout={500}>
      <Box 
        sx={{ 
          textAlign: 'center',
          py: 8,
          px: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 3,
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <InboxIcon 
          sx={{ 
            fontSize: 64, 
            color: theme.palette.primary.main,
            mb: 2,
            opacity: 0.8
          }} 
        />
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1
          }}
        >
          No Invitations Yet
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            maxWidth: 500,
            mx: 'auto',
            mb: 3
          }}
        >
          You haven't received any space invitations yet. When someone invites you to join their space, it will appear here.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GroupIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          Browse Spaces
        </Button>
      </Box>
    </Fade>
  );
};

const NoOrganizationInvitationsMessage = () => {
  const theme = useTheme();
  return (
    <Fade in timeout={500}>
      <Box 
        sx={{ 
          textAlign: 'center',
          py: 8,
          px: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 3,
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <BusinessIcon 
          sx={{ 
            fontSize: 64, 
            color: theme.palette.primary.main,
            mb: 2,
            opacity: 0.8
          }} 
        />
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1
          }}
        >
          No Organization Invitations Yet
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            maxWidth: 500,
            mx: 'auto',
            mb: 3
          }}
        >
          When someone invites you to join their organization, it will appear here.
        </Typography>
      </Box>
    </Fade>
  );
};

const LoginRequiredMessage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Fade in timeout={500}>
      <Box 
        sx={{ 
          textAlign: 'center',
          py: 8,
          px: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 3,
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <LoginIcon 
          sx={{ 
            fontSize: 64, 
            color: theme.palette.primary.main,
            mb: 2,
            opacity: 0.8
          }} 
        />
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1
          }}
        >
          Login Required
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            maxWidth: 500,
            mx: 'auto',
            mb: 3
          }}
        >
          Please log in to view your invitations. You'll be redirected back to this page after logging in.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<LoginIcon />}
          onClick={() => {
            localStorage.setItem('redirectAfterLogin', '/invitations');
            navigate('/login');
          }}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }
          }}
        >
          Log In
        </Button>
      </Box>
    </Fade>
  );
};

const Invitations = () => {
  const token = useSelector(state => state.token);
  const authorized = useSelector(state => state.authorized);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [orgInvitations, setOrgInvitations] = useState([]);
  const [orgInvitationsLoading, setOrgInvitationsLoading] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API_URL}/invitations`);
      setInvitations(response.data.invitations);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgInvitations = async () => {
    try {
      setOrgInvitationsLoading(true);
      const response = await axios.get(`${API_URL}/tenants/invitations`);
      setOrgInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching organization invitations:', error);
    } finally {
      setOrgInvitationsLoading(false);
    }
  };

  const handleSpaceAdded = () => {
    dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });
  };

  useEffect(() => {
    if (token && authorized) {
      fetchInvitations();
    }
  }, [token, authorized]);

  useEffect(() => {
    if (authorized && tabValue === 1) {
      fetchOrgInvitations();
    }
  }, [authorized, tabValue]);

  const handleInvitationUpdate = (invitationId) => {
    setInvitations(prev => prev.filter(inv => inv.invitation_id !== invitationId));
  };

  const handleOrgInvitationUpdate = (invitationId) => {
    setOrgInvitations(prev => prev.filter(inv => inv.id !== invitationId));
  };

  const displayInvitations = loading ? Array(3).fill(null) : invitations;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            mb: 2
          }}
        >
          Your Invitations
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Discover and join spaces and organizations. Manage your pending invitations below.
        </Typography>
      </Box>

      {!authorized ? (
        <LoginRequiredMessage />
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} aria-label="invitations tabs">
              <Tab icon={<GroupIcon />} iconPosition="start" label="Spaces" sx={{ textTransform: 'none', minHeight: 56 }} />
              <Tab icon={<BusinessIcon />} iconPosition="start" label="Organization" sx={{ textTransform: 'none', minHeight: 56 }} />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <>
              {loading ? (
                <Grid container spacing={3}>
                  {displayInvitations.map((_, index) => (
                    <Grid item xs={12} key={index}>
                      <InvitationSkeleton />
                    </Grid>
                  ))}
                </Grid>
              ) : invitations.length > 0 ? (
                <Grid container spacing={3}>
                  {invitations.map((invitation, index) => (
                    <Grid item xs={12} key={invitation.invitation_id ?? index}>
                      <InvitationCard 
                        invitation={invitation} 
                        onUpdate={handleInvitationUpdate}
                        onSpaceAdded={handleSpaceAdded}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <NoInvitationsMessage />
              )}
            </>
          )}

          {tabValue === 1 && (
            <>
              {orgInvitationsLoading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3].map((i) => (
                    <Grid item xs={12} key={i}>
                      <InvitationSkeleton />
                    </Grid>
                  ))}
                </Grid>
              ) : orgInvitations.length > 0 ? (
                <Grid container spacing={3}>
                  {orgInvitations.map((invitation, index) => (
                    <Grid item xs={12} key={invitation.id ?? index}>
                      <OrganizationInvitationCard 
                        invitation={invitation} 
                        onUpdate={handleOrgInvitationUpdate}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <NoOrganizationInvitationsMessage />
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default Invitations; 